import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@bizfintax.com");
  const [password, setPassword] = useState("Admin@123");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Logged in");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-10 justify-between min-h-screen">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_task-leads-mgmt/artifacts/ngyuaae9_WhatsApp%20Image%202026-04-27%20at%2023.23.32.jpeg"
            alt="BizFinTax"
            className="h-12 w-12 object-contain"
          />
          <div>
            <div className="font-bold text-base tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>BIZFINTAX</div>
            <div className="label-uppercase mt-0.5">An Initiative to Grow India</div>
          </div>
        </div>

        <div className="max-w-sm w-full mx-0 py-10">
          <div className="label-uppercase mb-3">Sign in / 01</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tighter mb-2" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Welcome back.
          </h1>
          <p className="text-sm text-zinc-600 mb-10 leading-relaxed">
            Access tasks, leads, attendance and document workflows for the BizFinTax operations team.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-uppercase block mb-2">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 focus:border-[#002FA7]"
                required
              />
            </div>
            <div>
              <label className="label-uppercase block mb-2">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1 focus:border-[#002FA7]"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-ikb text-white py-3 text-sm font-medium tracking-wide hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-200">
            <div className="label-uppercase mb-3">Demo credentials</div>
            <div className="space-y-1.5 text-xs font-mono text-zinc-600">
              <div>admin@bizfintax.com / Admin@123</div>
              <div>employee@bizfintax.com / Employee@123</div>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="border-t border-zinc-200 pt-4">
            <div className="label-uppercase text-zinc-400 mb-1">Founder</div>
            <div className="text-sm font-semibold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>CA Pradeep Rawani</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 pt-2">
            © BizFinTax · An Initiative to Grow India · Confidential
          </div>
        </div>
      </div>

      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundImage: "linear-gradient(rgba(0, 20, 60, 0.55), rgba(0, 20, 60, 0.78)), url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Top brand strip */}
        <div className="absolute top-0 left-0 right-0 px-10 py-8 flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_task-leads-mgmt/artifacts/ngyuaae9_WhatsApp%20Image%202026-04-27%20at%2023.23.32.jpeg"
            alt="BizFinTax"
            className="h-10 w-10 object-contain bg-white p-1"
          />
          <div className="text-white">
            <div className="font-bold text-base tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>BIZFINTAX</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-white/80 mt-0.5">An Initiative to Grow India</div>
          </div>
        </div>

        {/* Centered hero brand block */}
        <div className="absolute inset-0 flex items-center justify-center px-10">
          <div className="text-center text-white max-w-md">
            <div className="text-[11px] uppercase tracking-[0.4em] text-white/70 mb-4">Welcome to</div>
            <h2
              className="text-6xl xl:text-7xl font-bold tracking-tighter mb-5 drop-shadow-lg"
              style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}
            >
              BIZFINTAX
            </h2>
            <div className="w-16 h-px bg-white/60 mx-auto mb-5" />
            <div className="text-lg xl:text-xl uppercase tracking-[0.28em] font-semibold text-white">
              Centre of Business Excellence
            </div>
            <p className="mt-8 text-sm text-white/85 leading-relaxed">
              Disciplined finance, audit and tax operations — managed with precision, executed without friction.
            </p>
          </div>
        </div>

        {/* Founder card bottom */}
        <div className="absolute bottom-10 left-10 right-10 backdrop-blur-md bg-white/10 border border-white/30 p-5 max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.15em] text-white/70 mb-1">Founder</div>
          <div className="text-base font-semibold tracking-tight text-white" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            CA Pradeep Rawani
          </div>
        </div>
      </div>
    </div>
  );
}
